<template>
  <div v-if="documents.length" class="session-docs">
    <button
      type="button"
      class="session-docs__toggle"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="panelId"
      :aria-label="
        $t('Views.Stage.Agenda.AgendaDocuments.sessionDocumentsAriaLabel')
      "
      @click.stop="open = !open"
    >
      <i class="far fa-file-alt" aria-hidden="true"></i>
      {{
        open
          ? $t('Views.Stage.Agenda.AgendaDocuments.hideDocs')
          : $t('Views.Stage.Agenda.AgendaDocuments.showDocs')
      }}
    </button>

    <ul v-if="open" :id="panelId" class="session-docs__list">
      <li
        v-for="document in documents"
        :key="document.DocumentID"
        class="session-docs__item"
      >
        <div class="session-docs__info">
          <Moment
            class="session-docs__date"
            :value="document.uTime"
            format="DD.MM.YYYY"
          />
          <span class="session-docs__name">{{ document.Name }}</span>
        </div>
        <div class="session-docs__actions">
          <button
            v-if="canPreview(document) && isDesktop()"
            type="button"
            class="session-docs__btn session-docs__btn--secondary"
            aria-haspopup="true"
            :aria-label="`${$t('Views.Stage.Agenda.AgendaDocuments.show')} ${
              document.Name
            }`"
            @click.stop="onClickPreview(document)"
          >
            {{ $t('Views.Stage.Agenda.AgendaDocuments.show') }}
          </button>
          <a
            class="session-docs__btn session-docs__btn--primary"
            :href="document.Documentfile"
            target="_blank"
            rel="noopener noreferrer"
            download
            :aria-label="`${$t(
              'Views.Stage.Agenda.AgendaDocuments.download'
            )} ${document.Name}`"
            @click.stop="onClickDownload(document)"
          >
            {{ $t('Views.Stage.Agenda.AgendaDocuments.download') }}
          </a>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getApplicablePlatforms } from '@/utils';
import { documentPreviewMixin } from '@/mixins';
import { createAnalyticsEvent } from '@/helpers/ga-helper';

// Shared across every card instance: the Documents store only de-dupes concurrent
// fetches, so an event with no documents would otherwise refetch once per card.
let documentsRequested = false;

// Documents attached to a session card (Lobby, Program, My program).
//
// The link between a session and its documents is read from each document's `sessions`
// list. `session.docIds` is NOT used: it is a derived, non-persisted field that the live
// agenda endpoint (cached) never fills in, so it is always empty here.
//
// No feature-flag gate: getAllDocuments already returns nothing when the event's Documents
// setting is empty and filters hidden / group-restricted documents. The platform check only
// excludes documents explicitly restricted to a different platform; a document with no
// display option set is shown. Renders nothing when the session has no documents.
export default {
  name: 'SessionDocuments',
  mixins: [documentPreviewMixin],
  props: {
    session: { type: Object, required: true }
  },
  data() {
    return { open: false };
  },
  created() {
    if (!documentsRequested) {
      documentsRequested = true;
      this.fetchItems();
    }
  },
  computed: {
    ...mapGetters('Documents', { allDocuments: 'getItems' }),
    platforms() {
      return [...getApplicablePlatforms(this.$route), 'lobby'];
    },
    panelId() {
      return `session-docs-${this.session.id}`;
    },
    documents() {
      if (!this.session?.id) return [];
      return (this.allDocuments || []).filter((d) => {
        if (!d.sessions?.includes(this.session.id)) return false;
        const options = d.PlatformDisplayOptions;
        return (
          !options?.length || options.some((o) => this.platforms.includes(o))
        );
      });
    }
  },
  methods: {
    ...mapActions('Documents', ['fetchItems']),
    isDesktop() {
      return window.screen.width > 1024;
    },
    onClickDownload(doc) {
      createAnalyticsEvent(
        this.$gtag,
        'Download',
        'ProgramDocument',
        doc.DocumentID
      );
    },
    onClickPreview(doc) {
      createAnalyticsEvent(
        this.$gtag,
        'Preview',
        'ProgramDocument',
        doc.DocumentID
      );
      this.openPreview(doc);
    }
  }
};
</script>

<style scoped lang="scss">
// `display: contents` lets the toggle sit in the card's action row while the
// list wraps onto its own full-width row underneath.
.session-docs {
  display: contents;

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    background: #ffffff;
    color: var(--color-primary, #047873);
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;

    i {
      font-size: 10px;
    }

    &:hover {
      background: #f9fafb;
      border-color: var(--color-primary, #047873);
    }

    &:focus-visible {
      outline: 3px solid var(--color-primary, #047873);
      outline-offset: 2px;
    }
  }

  &__list {
    flex: 0 0 100%;
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 0;
    border-top: 1px solid #f1f5f9;
  }

  &__info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1 1 140px;
  }

  &__date {
    font-size: 11px;
    color: #64748b;
  }

  &__name {
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
    overflow-wrap: anywhere;
  }

  &__actions {
    display: flex;
    gap: 6px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 999px;
    border: 1.5px solid var(--color-primary, #047873);
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.15s;

    &:hover {
      opacity: 0.85;
    }

    &:focus-visible {
      outline: 3px solid var(--color-primary, #047873);
      outline-offset: 2px;
    }

    &--primary {
      background: var(--color-primary, #047873);
      color: #ffffff;
    }

    &--secondary {
      background: #ffffff;
      color: var(--color-primary, #047873);
    }
  }
}
</style>
